import { useCallback, useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { AlertCircle, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { LicitacionDetail } from "./LicitacionDetail";
import { licitacionesService } from "@/services/licitaciones.service";
import { ApiError } from "@/services/api/errors";
import { mercadoPublicoUrl } from "@/lib/licitaciones";
import type { Licitacion } from "@/types";

const cache = new Map<string, Licitacion>();

export function QuickViewDrawer({
  id,
  open,
  onOpenChange,
}: {
  id: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [data, setData] = useState<Licitacion | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<"unauthorized" | "not-found" | "generic" | null>(null);
  const load = useCallback(async () => {
    if (!id) return;
    const cached = cache.get(id);
    if (cached) {
      setData(cached);
      setError(null);
    }
    setLoading(true);
    setError(null);
    try {
      const result = await licitacionesService.getById(id);
      if (!result) throw new Error("not-found");
      cache.set(id, result);
      setData(result);
    } catch (caught) {
      if (caught instanceof ApiError && (caught.status === 401 || caught.status === 403)) {
        setError("unauthorized");
      } else if (caught instanceof ApiError && caught.status === 404) {
        setError("not-found");
      } else {
        setError("generic");
      }
      if (!cached) setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => {
    if (open) void load();
  }, [load, open]);
  const update = (next: Licitacion) => {
    cache.set(next.id, next);
    setData(next);
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto pb-20 sm:max-w-3xl">
        <SheetHeader className="pr-8">
          <SheetTitle>Vista rápida</SheetTitle>
          <SheetDescription>
            Resumen operativo con datos oficiales y gestión interna.
          </SheetDescription>
        </SheetHeader>
        {loading && !data && (
          <div className="space-y-3 py-8" aria-label="Cargando detalle">
            <div className="h-6 animate-pulse rounded bg-muted" />
            <div className="h-24 animate-pulse rounded bg-muted" />
            <div className="h-40 animate-pulse rounded bg-muted" />
          </div>
        )}
        {error && (
          <div className="grid place-items-center gap-3 py-16 text-center">
            <AlertCircle className="size-8 text-destructive" />
            <div>
              <p className="font-medium">
                {error === "unauthorized"
                  ? "Tu sesión no permite cargar esta licitación"
                  : error === "not-found"
                    ? "Licitación no encontrada"
                    : "No se pudo cargar la licitación"}
              </p>
              <p className="text-sm text-muted-foreground">
                {error === "unauthorized"
                  ? "Vuelve a iniciar sesión y luego inténtalo nuevamente."
                  : error === "not-found"
                    ? "El registro pudo haber sido eliminado o el enlace ya no es válido."
                    : "Revisa la conexión e inténtalo nuevamente."}
              </p>
            </div>
            <Button variant="outline" onClick={load}>
              Reintentar
            </Button>
          </div>
        )}
        {data && (
          <>
            <div className="sticky top-0 z-20 -mx-6 my-4 flex items-center justify-between border-y border-border bg-background/95 px-6 py-2 backdrop-blur">
              <span className="text-xs text-muted-foreground">
                {loading ? "Actualizando datos…" : "Datos sincronizados"}
              </span>
              <div className="flex gap-2">
                <Button asChild size="sm" variant="outline" className="hidden sm:inline-flex">
                  <a href={mercadoPublicoUrl(data.codigo)} target="_blank" rel="noreferrer">
                    Ficha oficial <ExternalLink />
                  </a>
                </Button>
                <Button asChild size="sm">
                  <Link to="/licitaciones/$id" params={{ id: data.id }}>
                    Abrir página completa <ExternalLink />
                  </Link>
                </Button>
              </div>
            </div>
            <LicitacionDetail licitacion={data} compact onChange={update} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
